import { useState, useCallback, useEffect } from 'react';
import { PROJECT_TYPES, type ProjectTypeValue } from '@/utils/projectTypeColors';

const STORAGE_KEY = 'project-table-type-badges';

const DEFAULT_VISIBLE: Record<ProjectTypeValue, boolean> = Object.fromEntries(
  PROJECT_TYPES.map((t) => [t, false])
) as Record<ProjectTypeValue, boolean>;

function loadFromStorage(): Record<ProjectTypeValue, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE };
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    const result = { ...DEFAULT_VISIBLE };
    for (const key of PROJECT_TYPES) {
      if (typeof parsed[key] === 'boolean') result[key] = parsed[key];
    }
    return result;
  } catch {
    return { ...DEFAULT_VISIBLE };
  }
}

function saveToStorage(value: Record<ProjectTypeValue, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function useTypeBadgeVisibility(): {
  typeBadgesEnabled: Record<ProjectTypeValue, boolean>;
  toggleTypeBadge: (type: ProjectTypeValue) => void;
} {
  const [typeBadgesEnabled, setTypeBadgesEnabled] = useState<Record<ProjectTypeValue, boolean>>(loadFromStorage);

  useEffect(() => {
    saveToStorage(typeBadgesEnabled);
  }, [typeBadgesEnabled]);

  const toggleTypeBadge = useCallback((type: ProjectTypeValue) => {
    setTypeBadgesEnabled((prev) => ({ ...prev, [type]: !prev[type] }));
  }, []);

  return { typeBadgesEnabled, toggleTypeBadge };
}
